import React, { useMemo, useState } from 'react';
import { ArrowLeftIcon, FileIcon, MailIcon, MapPinIcon, PencilIcon, PhoneIcon, PlusIcon } from 'lucide-react';
import { Employee, ProCustomer, ProInvoice, ProJob, Quote, TimeEntry } from '../../types/pro';
import { INVOICE_STATUS, JOB_STATUS, QUOTE_STATUS, formatDate, kr, quoteTotal } from '../../utils/pro';
import { invoiceTotal } from '../../utils/invoice';
import { StatusChip } from '../../components/pro/StatusChip';
import { EmptyState } from '../../components/pro/EmptyState';
import { CustomerFormModal } from '../../components/pro/CustomerFormModal';
import { contactsOf } from '../../utils/contacts';
interface ProCustomerDetailProps {
  customer: ProCustomer;
  jobs: ProJob[];
  quotes: Quote[];
  invoices: ProInvoice[];
  timeEntries: TimeEntry[];
  employees: Employee[];
  onBack: () => void;
  onOpenJob: (jobId: string) => void;
  onOpenQuote: (quoteId: string) => void;
  onOpenInvoice: (invoiceId: string) => void;
  onUpdate: (changes: Partial<ProCustomer>) => void;
  /** Creates a job for this customer, no quote required. */
  onNewJob: () => void;
}
type Tab = 'overblik' | 'opgaver' | 'tilbud' | 'fakturaer' | 'timer' | 'dokumenter' | 'noter';
const TABS: {
  id: Tab;
  label: string;
}[] = [{
  id: 'overblik',
  label: 'Overblik'
}, {
  id: 'opgaver',
  label: 'Opgaver'
}, {
  id: 'tilbud',
  label: 'Tilbud'
}, {
  id: 'fakturaer',
  label: 'Fakturaer'
}, {
  id: 'timer',
  label: 'Timer'
}, {
  id: 'dokumenter',
  label: 'Dokumenter'
}, {
  id: 'noter',
  label: 'Noter'
}];
export function ProCustomerDetail({
  customer,
  jobs,
  quotes,
  invoices,
  timeEntries,
  employees,
  onBack,
  onOpenJob,
  onOpenQuote,
  onOpenInvoice,
  onUpdate,
  onNewJob
}: ProCustomerDetailProps) {
  const [tab, setTab] = useState<Tab>('overblik');
  const [editing, setEditing] = useState(false);
  const people = contactsOf(customer);
  const own = useMemo(() => ({
    jobs: jobs.filter(job => job.customerId === customer.id),
    quotes: quotes.filter(quote => quote.customerId === customer.id),
    invoices: invoices.filter(invoice => invoice.customerId === customer.id)
  }), [jobs, quotes, invoices, customer.id]);
  const jobIds = own.jobs.map(job => job.id);
  const hours = timeEntries.filter(entry => jobIds.includes(entry.jobId));
  const totalHours = hours.reduce((sum, entry) => sum + entry.hours, 0);
  const revenue = own.invoices.filter(invoice => invoice.status !== 'kladde').reduce((sum, invoice) => sum + invoiceTotal(invoice), 0);
  const timeline = useMemo(() => {
    const entries: {
      id: string;
      date: string;
      text: string;
    }[] = [];
    own.quotes.forEach(quote => entries.push({
      id: `q-${quote.id}`,
      date: quote.createdDate,
      text: `Tilbud ${quote.quoteNo} — ${QUOTE_STATUS[quote.status].label} · ${kr(quoteTotal(quote))}`
    }));
    own.jobs.forEach(job => entries.push({
      id: `j-${job.id}`,
      date: job.start ?? '',
      text: `Opgave ${job.jobNo} — ${job.title} · ${JOB_STATUS[job.status].label}`
    }));
    own.invoices.forEach(invoice => entries.push({
      id: `i-${invoice.id}`,
      date: invoice.invoiceDate,
      text: `Faktura ${invoice.invoiceNo} — ${INVOICE_STATUS[invoice.status].label} · ${kr(invoiceTotal(invoice))}`
    }));
    return entries.filter(entry => entry.date).sort((a, b) => b.date.localeCompare(a.date));
  }, [own]);
  const card = 'rounded-xl border border-stone-200 bg-white';
  return <div data-id="element-1092" data-mp="2lfhf926qvcd1">
      <button onClick={onBack} className="mb-4 inline-flex items-center gap-2 text-sm font-medium text-stone-500 transition-colors hover:text-stone-900" data-id="element-1093" data-mp="2lfr6su7ftrku">
        <ArrowLeftIcon className="h-4 w-4" data-id="element-1094" data-mp="r5ds4hvg0emt" />
        Alle kunder
      </button>

      <header className="mb-5 flex flex-wrap items-start justify-between gap-4" data-id="element-1095" data-mp="w3f98x9b63vn">
        <div className="min-w-0" data-id="element-1096" data-mp="ro5js395z7i">
          <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-brand-700" data-id="element-1097" data-mp="qmisqcvjo9y">
            {customer.customerNo}
            <span className="rounded-full bg-stone-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-stone-500" data-id="element-1098" data-mp="ar6oc57ffexz">
              {customer.type === 'firma' ? 'Firma' : 'Privat'}
            </span>
          </p>
          <h1 className="mt-1 text-2xl font-bold tracking-tight text-stone-900 sm:text-3xl" data-id="element-1099" data-mp="3e85caqdznnhq">
            {customer.name}
          </h1>
          <p className="mt-1 text-sm text-stone-500" data-id="element-1100" data-mp="3966a3jtziwmd">
            {[customer.address, `${customer.postalCode} ${customer.city}`.trim()].filter(part => part && part.trim()).join(', ')}
            {customer.cvr && ` · CVR ${customer.cvr}`}
          </p>
        </div>
        <button onClick={() => setEditing(true)} className="inline-flex items-center gap-2 rounded-lg border border-stone-200 bg-white px-3.5 py-2 text-sm font-semibold text-stone-700 transition-colors hover:bg-stone-50" data-id="element-1101" data-mp="2vern71wyiba1">
          <PencilIcon className="h-4 w-4" data-id="element-1102" data-mp="1aqqqc6rjozwt" />
          Rediger oplysninger
        </button>
      </header>

      <CustomerFormModal isOpen={editing} onClose={() => setEditing(false)} customer={customer} nextCustomerNo={customer.customerNo} onSave={changes => onUpdate(changes)} data-id="element-1103" data-mp="w2m0miu2ro4j" />

      <div role="tablist" aria-label="Kundeoplysninger" className="mb-5 flex gap-1 overflow-x-auto rounded-xl border border-stone-200 bg-white p-1" data-id="element-1104" data-mp="1oqrqfjzzk3b8">
        {TABS.map(item => <button key={item.id} role="tab" aria-selected={tab === item.id} onClick={() => setTab(item.id)} className={`whitespace-nowrap rounded-lg px-3.5 py-2 text-sm font-medium transition-colors ${tab === item.id ? 'bg-brand-50 text-brand-700' : 'text-stone-500 hover:text-stone-900'}`} data-id="element-1105" data-mp="1frmqc98s01wy">
            {item.label}
          </button>)}
      </div>

      {tab === 'overblik' && <div className="grid grid-cols-1 gap-5 lg:grid-cols-3" data-id="element-1106" data-mp="218uakxwe9ewr">
          <div className="space-y-5 lg:col-span-2" data-id="element-1107" data-mp="21713jhhvcso8">
            <section className={`${card} grid grid-cols-2 gap-px overflow-hidden bg-stone-200 lg:grid-cols-4`} data-id="element-1108" data-mp="1waaq71xg8b6i">
              {[{
            label: 'Opgaver',
            value: own.jobs.length
          }, {
            label: 'Tilbud',
            value: own.quotes.length
          }, {
            label: 'Registrerede timer',
            value: `${totalHours.toLocaleString('da-DK')} t`
          }, {
            label: 'Omsætning',
            value: kr(revenue)
          }].map(stat => <div key={stat.label} className="bg-white p-4" data-id="element-1109" data-mp="3cmps2dartzr5">
                  <p className="text-[11px] font-semibold uppercase tracking-wider text-stone-400" data-id="element-1110" data-mp="37p642lo9u8m0">
                    {stat.label}
                  </p>
                  <p className="mt-1 text-lg font-bold tabular-nums text-stone-900" data-id="element-1111" data-mp="3g5okjyty5nmk">
                    {stat.value}
                  </p>
                </div>)}
            </section>

            <section className={`${card} overflow-hidden`} data-id="element-1112" data-mp="3oz0h4grb620u">
              <header className="border-b border-stone-100 px-5 py-3.5" data-id="element-1113" data-mp="3txl95swepfe7">
                <h2 className="text-base font-semibold text-stone-900" data-id="element-1114" data-mp="2x7c8a47qnxmr">
                  Historik
                </h2>
              </header>
              {timeline.length === 0 ? <EmptyState title="Ingen historik endnu" data-id="element-1115" data-mp="31ah7nxwivj76" /> : <ol className="divide-y divide-stone-100" data-id="element-1116" data-mp="366fw1lhup17l">
                  {timeline.map(entry => <li key={entry.id} className="flex items-start gap-3 px-5 py-3" data-id="element-1117" data-mp="3b4r3jpkoazmn">
                      <span className="mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-stone-300" data-id="element-1118" data-mp="qftiqixx53i4" />
                      <span className="min-w-0 flex-1 text-sm text-stone-700" data-id="element-1119" data-mp="1blozgqyyxj6f">
                        {entry.text}
                      </span>
                      <span className="flex-shrink-0 text-xs text-stone-400" data-id="element-1120" data-mp="17gz556jykcnm">
                        {formatDate(entry.date)}
                      </span>
                    </li>)}
                </ol>}
            </section>
          </div>

          <section className={`${card} p-5`} data-id="element-1121" data-mp="1v7bn7a8x3prx">
            <h2 className="mb-4 text-base font-semibold text-stone-900" data-id="element-1122" data-mp="1q9jsk8gw1pk1">
              Kontakt
            </h2>
            <ul className="space-y-3 text-sm" data-id="element-1123" data-mp="249ukzfxujc7b">
              <li data-id="element-1124" data-mp="248szehj7f7bc">
                <a href={`tel:${customer.phone.replace(/\s/g, '')}`} className="flex items-center gap-2.5 text-stone-700 transition-colors hover:text-brand-700" data-id="element-1125" data-mp="246ymnqi36lic">
                  <PhoneIcon className="h-4 w-4 text-stone-400" data-id="element-1126" data-mp="4w8e7v55f6id" />
                  {customer.phone}
                </a>
              </li>
              <li data-id="element-1127" data-mp="2gklkf26u7r2k">
                <a href={`mailto:${customer.email}`} className="flex items-center gap-2.5 break-all text-stone-700 transition-colors hover:text-brand-700" data-id="element-1128" data-mp="2gk3qic7ndoxt">
                  <MailIcon className="h-4 w-4 flex-shrink-0 text-stone-400" data-id="element-1129" data-mp="27r07zpi5alwb" />
                  {customer.email}
                </a>
              </li>
              <li data-id="element-1130" data-mp="1xst2q3x7xsa5">
                <a href={`https://www.google.com/maps/search/${encodeURIComponent(`${customer.address}, ${customer.postalCode} ${customer.city}`)}`} target="_blank" rel="noreferrer" className="flex items-start gap-2.5 text-stone-700 transition-colors hover:text-brand-700" data-id="element-1131" data-mp="1xsax4goo21pk">
                  <MapPinIcon className="mt-0.5 h-4 w-4 flex-shrink-0 text-stone-400" data-id="element-1132" data-mp="3j7qfdbyb1jri" />
                  <span data-id="element-1133" data-mp="3n6zaak52znon">
                    {customer.address}
                    <br data-id="element-1134" data-mp="31cb7hkqiw15p" />
                    {customer.postalCode} {customer.city}
                  </span>
                </a>
              </li>
              {customer.leadSource && <li data-id="element-1135" data-mp="2rdvefc7xqhtk">
                  <span className="block text-xs uppercase tracking-wide text-stone-400" data-id="element-1136" data-mp="2redj8x8ny4d3">
                    Kom fra
                  </span>
                  <span className="text-stone-900" data-id="element-1137" data-mp="2wdhoxdwdpjyo">{customer.leadSource}</span>
                </li>}
              <li data-id="element-1138" data-mp="3k0g4888rilv0">
                <span className="block text-xs uppercase tracking-wide text-stone-400" data-id="element-1139" data-mp="3p1ly49e5sf64">
                  Kunde siden
                </span>
                <span className="text-stone-900" data-id="element-1140" data-mp="3b0t3zprrrm3j">
                  {formatDate(customer.createdDate)}
                </span>
              </li>
            </ul>

            {/* Everyone you can reach at this customer */}
            {people.length > 0 && <div className="mt-5 border-t border-stone-100 pt-4" data-id="element-1141" data-mp="23oavgtbs7kka">
                <h3 className="mb-3 text-xs font-semibold uppercase tracking-wide text-stone-400" data-id="element-1142" data-mp="23plzmu952ubx">
                  {people.length === 1 ? 'Kontaktperson' : `Kontaktpersoner (${people.length})`}
                </h3>
                <ul className="space-y-3" data-id="element-1143" data-mp="2637kv8tscbvv">
                  {people.map(contact => <li key={contact.id} className="rounded-lg border border-stone-200 p-3" data-id="element-1144" data-mp="213bife0qksif">
                      <div className="flex items-start gap-2" data-id="element-1145" data-mp="1dc62kyusq084">
                        <span className="min-w-0 flex-1" data-id="element-1146" data-mp="18gic8g4kv111">
                          <span className="block truncate text-sm font-medium text-stone-900" data-id="element-1147" data-mp="1n3tmmep0wezk">
                            {contact.name}
                          </span>
                          {contact.role && <span className="block text-xs text-stone-500" data-id="element-1148" data-mp="un992x783bqy">
                              {contact.role}
                            </span>}
                        </span>
                        {contact.primary && people.length > 1 && <span className="flex-shrink-0 rounded-full bg-brand-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-brand-700" data-id="element-1149" data-mp="1je6qpvdzttig">
                            Primær
                          </span>}
                      </div>
                      <div className="mt-2 space-y-1.5" data-id="element-1150" data-mp="1t62ot6g2xjmo">
                        {contact.phone && <a href={`tel:${contact.phone.replace(/\s/g, '')}`} className="flex items-center gap-2 text-sm text-stone-600 transition-colors hover:text-brand-700" data-id="element-1151" data-mp="wgbt53w1xqfa">
                            <PhoneIcon className="h-3.5 w-3.5 flex-shrink-0 text-stone-400" data-id="element-1152" data-mp="1afbkwkev2lfo" />
                            {contact.phone}
                          </a>}
                        {contact.email && <a href={`mailto:${contact.email}`} className="flex items-center gap-2 break-all text-sm text-stone-600 transition-colors hover:text-brand-700" data-id="element-1153" data-mp="a5karwt3sxgd">
                            <MailIcon className="h-3.5 w-3.5 flex-shrink-0 text-stone-400" data-id="element-1154" data-mp="efturjvb2iw" />
                            {contact.email}
                          </a>}
                        {contact.notes && <p className="text-xs italic text-stone-500" data-id="element-1155" data-mp="3ni3vo1pvng30">
                            {contact.notes}
                          </p>}
                      </div>
                    </li>)}
                </ul>
              </div>}
          </section>
        </div>}

      {tab === 'opgaver' && <section className={`${card} overflow-hidden`} data-id="element-1156" data-mp="1budy8i43p0ds">
          <header className="flex items-center justify-between gap-3 border-b border-stone-100 px-5 py-3.5" data-id="element-1157" data-mp="16w2rig93r5zs">
            <h2 className="text-sm font-semibold text-stone-900" data-id="element-1158" data-mp="1ew90rt26u9r5">
              {own.jobs.length === 0 ? 'Opgaver' : `${own.jobs.length} ${own.jobs.length === 1 ? 'opgave' : 'opgaver'}`}
            </h2>
            <button onClick={onNewJob} className="inline-flex items-center gap-1.5 rounded-lg bg-brand-600 px-3 py-1.5 text-sm font-semibold text-white transition-colors hover:bg-brand-700" data-id="element-1159" data-mp="19xfkjsvio4na">
              <PlusIcon className="h-4 w-4" data-id="element-1160" data-mp="10xsuu4c5gna2" />
              Ny opgave
            </button>
          </header>
          {own.jobs.length === 0 ? <EmptyState title="Ingen opgaver på denne kunde" description="Opret regningsarbejde direkte — der behøver ikke være et tilbud." data-id="element-1161" data-mp="m5qr296yla4r" /> : <ul className="divide-y divide-stone-100" data-id="element-1162" data-mp="d99jth1ptwea">
              {own.jobs.map(job => <li key={job.id} data-id="element-1163" data-mp="haussrb4pqj7">
                  <button onClick={() => job.id && onOpenJob(job.id)} className="flex w-full items-center gap-4 px-5 py-3.5 text-left transition-colors hover:bg-stone-50" data-id="element-1164" data-mp="3upc6dwodmi83">
                    <span className="min-w-0 flex-1" data-id="element-1165" data-mp="7lxn5p1319jn">
                      <span className="block truncate text-sm font-medium text-stone-900" data-id="element-1166" data-mp="2mbapht2vxbw">
                        {job.title}
                      </span>
                      <span className="block text-xs text-stone-500" data-id="element-1167" data-mp="hhr6psd7y3ax">
                        {job.jobNo} · {formatDate(job.start ?? '')}
                      </span>
                    </span>
                    <StatusChip meta={JOB_STATUS[job.status]} data-id="element-1168" data-mp="qb4bpsok4vdd" />
                  </button>
                </li>)}
            </ul>}
        </section>}

      {tab === 'tilbud' && <section className={`${card} overflow-hidden`} data-id="element-1169" data-mp="10a3amjjitcn6">
          {own.quotes.length === 0 ? <EmptyState title="Ingen tilbud på denne kunde" data-id="element-1170" data-mp="vcjwdjmtozv8" /> : <ul className="divide-y divide-stone-100" data-id="element-1171" data-mp="3p4022fnj6hw5">
              {own.quotes.map(quote => <li key={quote.id} data-id="element-1172" data-mp="3t53gqytue7vs">
                  <button onClick={() => quote.id && onOpenQuote(quote.id)} className="flex w-full items-center gap-4 px-5 py-3.5 text-left transition-colors hover:bg-stone-50" data-id="element-1173" data-mp="3t1xrnjl7r602">
                    <span className="min-w-0 flex-1" data-id="element-1174" data-mp="bobxs64q0wy9">
                      <span className="block truncate text-sm font-medium text-stone-900" data-id="element-1175" data-mp="bpwo1jk05tqb">
                        {quote.title}
                      </span>
                      <span className="block text-xs text-stone-500" data-id="element-1176" data-mp="kpjtqhjrllp6">
                        {quote.quoteNo} · {formatDate(quote.createdDate)}
                      </span>
                    </span>
                    <span className="text-sm font-semibold tabular-nums text-stone-900" data-id="element-1177" data-mp="2mgwtytasf5ew">
                      {kr(quoteTotal(quote))}
                    </span>
                    <StatusChip meta={QUOTE_STATUS[quote.status]} data-id="element-1178" data-mp="2jvfcvv6u8uuk" />
                  </button>
                </li>)}
            </ul>}
        </section>}

      {tab === 'fakturaer' && <section className={`${card} overflow-hidden`} data-id="element-1179" data-mp="2a52w8e259ltp">
          {own.invoices.length === 0 ? <EmptyState title="Ingen fakturaer på denne kunde" data-id="element-1180" data-mp="2571b7nl5rcfl" /> : <ul className="divide-y divide-stone-100" data-id="element-1181" data-mp="32u9paxitxj7k">
              {own.invoices.map(invoice => <li key={invoice.id} data-id="element-1182" data-mp="2xvgdd9j6y09i">
                  <button onClick={() => invoice.id && onOpenInvoice(invoice.id)} className="flex w-full items-center gap-4 px-5 py-3.5 text-left transition-colors hover:bg-stone-50" data-id="element-1183" data-mp="2xwi1ofp7wj96">
                    <span className="min-w-0 flex-1" data-id="element-1184" data-mp="2owv1fx885jhp">
                      <span className="block text-sm font-medium text-stone-900" data-id="element-1185" data-mp="1hhq5rssv4tv4">
                        {invoice.invoiceNo}
                      </span>
                      <span className="block text-xs text-stone-500" data-id="element-1186" data-mp="3ckd4vdnfzs8f">
                        Forfalder {formatDate(invoice.dueDate)}
                      </span>
                    </span>
                    <span className="text-sm font-semibold tabular-nums text-stone-900" data-id="element-1187" data-mp="33jeyiopj78mo">
                      {kr(invoiceTotal(invoice))}
                    </span>
                    <StatusChip meta={INVOICE_STATUS[invoice.status]} data-id="element-1188" data-mp="38j1lhpv5w38o" />
                  </button>
                </li>)}
            </ul>}
        </section>}

      {tab === 'timer' && <section className={`${card} overflow-hidden`} data-id="element-1189" data-mp="46e8wc0h06ux">
          {hours.length === 0 ? <EmptyState title="Ingen registrerede timer" data-id="element-1190" data-mp="3mi13ieo65jvv" /> : <>
              <ul className="divide-y divide-stone-100" data-id="element-1191" data-mp="3rfjcg526rz3v">
                {hours.map(entry => {
            const job = own.jobs.find(j => j.id === entry.jobId);
            const employee = employees.find(e => e.id === entry.employeeId);
            return <li key={entry.id} className="flex items-center gap-4 px-5 py-3" data-id="element-1192" data-mp="x4q9ppdqfcdt">
                      <span className="min-w-0 flex-1" data-id="element-1193" data-mp="16vku0o7f41wm">
                        <span className="block truncate text-sm text-stone-900" data-id="element-1194" data-mp="11y2u1nbdox0r">
                          {job?.title ?? 'Opgave'}
                        </span>
                        <span className="block text-xs text-stone-500" data-id="element-1195" data-mp="e8i8cwnublns">
                          {employee?.name ?? 'Ukendt'} · {entry.workType} ·{' '}
                          {formatDate(entry.date)}
                        </span>
                      </span>
                      <span className="text-sm font-semibold tabular-nums text-stone-900" data-id="element-1196" data-mp="j71uax2foxg5">
                        {entry.hours.toLocaleString('da-DK')} t
                      </span>
                    </li>;
          })}
              </ul>
              <div className="flex items-center justify-between border-t border-stone-200 bg-stone-50 px-5 py-3" data-id="element-1197" data-mp="2ur6spy4t9j6g">
                <span className="text-sm font-medium text-stone-600" data-id="element-1198" data-mp="2psdeg3hp9o58">
                  I alt
                </span>
                <span className="text-sm font-bold tabular-nums text-stone-900" data-id="element-1199" data-mp="33s4yakuwdyr5">
                  {totalHours.toLocaleString('da-DK')} timer
                </span>
              </div>
            </>}
        </section>}

      {tab === 'dokumenter' && <EmptyState icon={FileIcon} title="Ingen dokumenter" description="Tegninger, aftaler og rapporter samles her når de lægges på en opgave." data-id="element-1200" data-mp="38qzerg5e7b83" />}

      {tab === 'noter' && <section className={`${card} p-5`} data-id="element-1201" data-mp="3igizkrco1f5l">
          <label className="block" data-id="element-1202" data-mp="3ihkljqv7zm23">
            <span className="mb-2 block text-sm font-medium text-stone-700" data-id="element-1203" data-mp="awie6jqd8z2">
              Interne noter om kunden
            </span>
            <textarea value={customer.notes} onChange={e => onUpdate({
          notes: e.target.value
        })} rows={8} placeholder="Adgangsforhold, aftaler, hvad man skal huske…" className="w-full rounded-xl border border-stone-200 px-4 py-3 text-sm leading-relaxed outline-none focus:border-transparent focus:ring-2 focus:ring-brand-500" data-id="element-1204" data-mp="3riil9wyml7ev" />
          </label>
        </section>}
    </div>;
}